import { useEffect, useState } from 'react';
import IconAttention from '../../icons/attention';
import IconBlock from '../../icons/block';
import IconSelected from '../../icons/selected';
import IconLoader from '../../icons/loader';
import IconClock from '../../icons/clock';
import formatDate from '../../../helpers/format-date';
import { DocumentStatus } from '../types';
import DocumentPreview from './document-preview';
import DocumentControls from './documents-controls';
import DocumentComments from './document-comments';
import { DocumentSidepanelTypes } from './types';

import styles from './documents.module.css';

export default function DocumentSidePanel({
  document,
  documentFileIsLoading,
  downloadInProgress,
  imgLink,
  isLoading,
  setDeletePopupVisible,
  onMove,
  moveInProgress,
  onDownload,
  deleteIsVisible,
  customerType,
  commentsVisible,
  sendComment,
  editComment,
  editCommentInProgress,
  editCommentIsSuccess,
  deleteComment,
  deleteCommentInProgress,
  commentInProgress,
  activePermissions,
  onDownloadSuccess,
  glTransaction,
}: DocumentSidepanelTypes) {
  const [downloadButtonIsActive, setDownloadButtonIsActive] = useState(false);

  useEffect(() => {
    if (onDownloadSuccess) {
      setDownloadButtonIsActive(false);
    }
  }, [onDownloadSuccess]);

  let content;

  if (isLoading) {
    content = (
      <div className={styles.documentInfoLoaderWrapper}>
        <IconLoader className={styles.documentInfoLoader} />
      </div>
    );
  } else {
    let statusIcon: React.JSX.Element;

    if (document?.status === DocumentStatus.ACCEPTED) {
      statusIcon = (
        <div
          className={`${styles.documentAccepted} ${styles.documentInfoAccepted}`}
        >
          <IconSelected
            className={styles.documentStatusIcon}
            height={9}
            width={13}
          />
        </div>
      );
    } else if (document?.status === DocumentStatus.EXPIRED) {
      statusIcon = (
        <div className={styles.documentExpired}>
          <IconAttention />
        </div>
      );
    } else if (document?.status === DocumentStatus.REJECTED) {
      statusIcon = (
        <div className={styles.documentInfoRejected}>
          <IconBlock />
        </div>
      );
    } else {
      statusIcon = <div className={styles.documentNew} />;
    }

    let imageContent;

    if (
      !downloadButtonIsActive &&
      (documentFileIsLoading || downloadInProgress)
    ) {
      imageContent = <IconLoader />;
    } else if (imgLink) {
      imageContent = (
        <a
          href={imgLink}
          target="_blank"
          rel="noreferrer"
          aria-label="Document link"
        >
          <DocumentPreview className={styles.documentPreviewIcon} />
        </a>
      );

      if (document?.upload?.mimeType?.includes('image')) {
        imageContent = (
          <a href={imgLink} target="_blank" rel="noreferrer">
            <img
              src={imgLink}
              alt={document?.name}
              className={
                !downloadButtonIsActive &&
                (documentFileIsLoading || downloadInProgress || !imgLink)
                  ? styles.imageHidden
                  : ''
              }
            />
          </a>
        );
      }
    }

    content = (
      <>
        <div className={styles.documentInfoBlock}>
          <div className={styles.documentInfoPreview}>{imageContent}</div>

          <DocumentControls
            group={document?.category}
            setDeletePopupVisible={setDeletePopupVisible}
            onMove={onMove}
            moveInProgress={moveInProgress}
            onDownload={onDownload}
            deleteIsVisible={deleteIsVisible}
            customerType={customerType}
            documentId={document?.id}
            activePermissions={activePermissions}
            setDownloadButtonIsActive={setDownloadButtonIsActive}
            downloadInProgress={documentFileIsLoading || downloadInProgress}
            downloadButtonIsActive={downloadButtonIsActive}
            glTransaction={glTransaction}
          />
        </div>
        <div className={styles.documentInfoBlock}>
          <div className={styles.documentInfoStatusWrapper}>
            <div className={styles.documentInfoStatus}>
              {document?.status}
              {statusIcon}
            </div>
            <div className={styles.documentInfoDates}>
              <div
                className={`${styles.documentInfoDate} ${
                  document?.status === DocumentStatus.EXPIRED
                    ? styles.documentDateExpired
                    : ''
                }`}
              >
                <div>Exp. date:</div>
                {document?.expirationDate
                  ? new Date(document?.expirationDate).toLocaleDateString()
                  : '-'}
                {document?.status === DocumentStatus.EXPIRED && (
                  <IconClock className={styles.expiredIcon} />
                )}
              </div>
              <div className={styles.documentInfoDate}>
                <div>Loaded:</div>
                {document?.createdAt
                  ? formatDate(document?.createdAt, true)
                  : '-'}
              </div>
            </div>
          </div>
        </div>
        {/* {info?.status === CustomerDocumentStatus.NEW && (
          <DocumentInfoStatusNew
            name={userName as string}
            message={info?.warningMessage}
            title="Check for errors"
            description="If there are errors, describe the problem and send a request to the client in the chat to resend it."
            buttonText="Describe errors"
            icon={<IconAttention className={cn.documentNewErrorIcon} />}
            sendMessage={sendMessage}
          />
        )} */}
        {/* {info.status === CustomerDocumentStatus.ACCEPTED &&
          info?.error?.type === DocumentErrorType.EXPIRED_SOON && (
            <DocumentInfoStatusNew
              name={userName as string}
              message={info?.warningMessage}
              title="The document will expire soon"
              buttonText="Remind the client to update the document"
              sendMessage={sendMessage}
              withTemplateMessage
            />
          )} */}
        {/* {info?.status === CustomerDocumentStatus.EXPIRED && (
          <DocumentError
            error={{
              title: 'The document has expired',
              description: 'Need to request a new document from the client',
            }}
          >
            <ButtonSecondary
              text="Request Document"
              className="button-white"
              bold
            />
          </DocumentError>
        )} */}
        <DocumentComments
          comments={document?.commentsList}
          sendComment={sendComment}
          editComment={editComment}
          editCommentInProgress={editCommentInProgress}
          editCommentIsSuccess={editCommentIsSuccess}
          deleteComment={deleteComment}
          deleteCommentInProgress={deleteCommentInProgress}
          commentInProgress={commentInProgress}
          documentId={document?.id}
          commentsVisible={commentsVisible}
        />
      </>
    );
  }

  return content;
}
